import Button from "pages/configurator/components/button";
import Input from "pages/configurator/components/input";
import { FunctionComponent, useState } from "react";
import { ColorValue, Customizer, CustomizerOption } from "@rromikas/configurio";
import MinusIcon from "@heroicons/react/24/outline/MinusIcon";
import uniqid from "uniqid";
import AssetPicker from "components/asset-picker/asset-picker";
import classNames from "classnames";
import { useId } from "react-id-generator";
import {
  ADD_OPTION_BUTTON_TEST_ID,
  DELETE_OPTION_BUTTON_TEST_ID,
  OPTIONS_LIST_TEST_ID,
  OPTION_ID_INPUT_TEST_ID,
  OPTION_LABEL_INPUT_TEST_ID,
  OPTION_PRICE_INPUT_TEST_ID,
  OPTION_VALUE_INPUT_TEST_ID,
} from "constants/test";

interface CustomizerComponentProps extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  value: Customizer;
  setValue: (val: Customizer) => void;
  type: "material" | "color";
}

const CustomizerComponent: FunctionComponent<CustomizerComponentProps> = ({ value, setValue, type, className, ...divProps }) => {
  const columns: (keyof CustomizerOption)[] = ["value", "id", "label", "price"];
  const [assetPickerIndex, setAssetPickerIndex] = useState<number>();

  const defaultOption: CustomizerOption = {
    label: "",
    value: type === "color" ? { type, value: "#3F8CFF" } : { type: "material", modelImageUrl: "", modelUrl: "" },
    id: uniqid(),
    price: 0,
  };

  const addOption = () => {
    if (type === "material") {
      setAssetPickerIndex(-1);
    } else {
      setValue({ ...value, options: [...value.options, defaultOption] });
    }
  };

  const optionsIds = useId(value.options.length);

  return (
    <div className={classNames(className, "text-sm")} {...divProps}>
      {assetPickerIndex == null ? null : (
        <AssetPicker
          assetType="material"
          onPick={(asset) => {
            if (assetPickerIndex === -1) {
              //new material
              setValue({
                ...value,
                options: [
                  ...value.options,
                  { ...defaultOption, value: { type: "material", modelImageUrl: asset.image_url, modelUrl: asset.url } },
                ],
              });
            } else {
              //update option material
              let arr = [...value.options];
              arr[assetPickerIndex].value = { type: "material", modelImageUrl: asset.image_url, modelUrl: asset.url };
              setValue({ ...value, options: arr });
            }

            setAssetPickerIndex(undefined);
          }}
          onClose={() => setAssetPickerIndex(undefined)}
        />
      )}
      <div className="flex justify-between items-center mb-4">
        <div className="first-letter:capitalize font-bold text-base">{type} options</div>
        <div className="flex items-center">
          <div className="mr-3">Label</div>
          <Input value={value.label} onChange={(e) => setValue({ ...value, label: e.target.value })} />
        </div>
      </div>
      <table className="w-full">
        <thead className="bg-gray-100">
          <tr>
            {columns.map((key, i) => (
              <th key={`column-${key}-${type}`} className="capitalize text-center py-1 px-2">
                {key}
              </th>
            ))}
            <th />
          </tr>
        </thead>
        <tbody data-testid={OPTIONS_LIST_TEST_ID(type)}>
          {value.options.map((option, i) => (
            <tr key={optionsIds[i]}>
              <td>
                {option.value.type === "color" ? (
                  <input
                    type="color"
                    value={option.value.value}
                    onChange={(e) => {
                      let arr = [...value.options];
                      (arr[i].value as ColorValue).value = e.target.value;
                      setValue({ ...value, options: arr });
                    }}
                    data-testid={OPTION_VALUE_INPUT_TEST_ID}
                  ></input>
                ) : (
                  <div
                    style={{ backgroundImage: `url(${option.value.modelImageUrl})` }}
                    className="h-[32px] w-[32px] bg-center bg-contain bg-no-repeat mx-auto"
                    onClick={() => {
                      setAssetPickerIndex(i);
                    }}
                    data-testid={OPTION_VALUE_INPUT_TEST_ID}
                  ></div>
                )}
              </td>
              <td className="px-1">
                <Input
                  className="w-full"
                  value={option.id}
                  data-testid={OPTION_ID_INPUT_TEST_ID}
                  onChange={(e) => {
                    let arr = [...value.options];
                    arr[i].id = e.target.value;
                    setValue({ ...value, options: arr });
                  }}
                />
              </td>
              <td className="px-1">
                <Input
                  className="w-full"
                  value={option.label}
                  data-testid={OPTION_LABEL_INPUT_TEST_ID}
                  onChange={(e) => {
                    let arr = [...value.options];
                    arr[i].label = e.target.value;
                    setValue({ ...value, options: arr });
                  }}
                />
              </td>
              <td className="px-1">
                <Input
                  className="w-full"
                  value={option.price}
                  data-testid={OPTION_PRICE_INPUT_TEST_ID}
                  onChange={(e) => {
                    let arr = [...value.options];
                    arr[i].price = +e.target.value;
                    setValue({ ...value, options: arr });
                  }}
                />
              </td>
              <td>
                <Button
                  data-testid={DELETE_OPTION_BUTTON_TEST_ID(type)}
                  className="px-3"
                  onClick={() => {
                    let arr = [...value.options];
                    arr.splice(i, 1);
                    setValue({ ...value, options: arr });
                  }}
                  variant="secondary"
                >
                  <MinusIcon className="w-5" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Button data-testid={ADD_OPTION_BUTTON_TEST_ID(type)} onClick={addOption} className="mt-3 w-full" variant="secondary">
        Add {type} option
      </Button>
    </div>
  );
};

export default CustomizerComponent;
